// Define maturity bucket ranges
const MATURITY_BUCKETS = [
  { key: '0-1', label: '0-1 years', min: 0, max: 1 },
  { key: '1-2', label: '1-2 years', min: 1, max: 2 },
  { key: '2-3', label: '2-3 years', min: 2, max: 3 },
  { key: '3-5', label: '3-5 years', min: 3, max: 5 },
  { key: '5-7', label: '5-7 years', min: 5, max: 7 },
  { key: '7-10', label: '7-10 years', min: 7, max: 10 },
  { key: '10-15', label: '10-15 years', min: 10, max: 15 },
  { key: '15-20', label: '15-20 years', min: 15, max: 20 },
  { key: '20+', label: '20+ years', min: 20, max: Infinity },
]

/**
 * Determines which maturity bucket a date belongs to
 * @param dateString A date string in YYYY-MM-DD format
 * @returns The bucket key for the date
 */
function getMaturityBucket(dateString: string): string {
  // Special cases
  if (dateString === 'cash') return 'cash'
  if (dateString === 'null' || !dateString) return 'null'

  const maturityDate = new Date(dateString)
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  if (isNaN(maturityDate.getTime())) return 'null'

  // Calculate years difference between maturity and today
  const today = new Date()

  const yearsDiff =
    (maturityDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 365.25)

  // If maturity date is in the past or today, put in 0-1 bucket
  if (yearsDiff <= 0) return '0-1'

  // Find the appropriate bucket
  const bucket = MATURITY_BUCKETS.find(
    (bucket) => yearsDiff > bucket.min && yearsDiff <= bucket.max,
  )

  return bucket ? bucket.key : '20+'
}

/**
 * Groups distribution data into maturity buckets
 * @param distributionData Original maturity distribution data
 * @param total Total value for percentage calculation
 * @returns Object with bucket keys and aggregated values
 */
export function groupIntoMaturityBuckets(
  distributionData: Record<string, number | undefined>,
  total: number,
): Record<string, number> {
  if (!distributionData) return {}

  // Initialize buckets with zeros
  const bucketValues: Record<string, number> = {}
  MATURITY_BUCKETS.forEach((bucket) => {
    bucketValues[bucket.key] = 0
  })

  // Also initialize special buckets
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  bucketValues['cash'] = 0
  // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
  bucketValues['null'] = 0

  // Sum values into appropriate buckets
  Object.entries(distributionData)
    .filter((entry): entry is [string, number] => entry[1] !== undefined)
    .forEach(([dateKey, value]) => {
      const bucketKey = getMaturityBucket(dateKey)
      bucketValues[bucketKey] = (bucketValues[bucketKey] ?? 0) + value
    })

  // Calculate percentages and filter out empty buckets
  return Object.fromEntries(
    Object.entries(bucketValues)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => [key, (value / total) * 100]),
  )
}

/**
 * Format a bucket key into a readable label
 * @param bucketKey The bucket key (e.g., "0-1", "20+")
 * @returns Formatted label (e.g., "0-1 years")
 */
export function formatMaturityBucketLabel(bucketKey: string): string {
  // Handle special cases
  if (bucketKey === 'cash') return 'Cash'
  if (bucketKey === 'null') return 'N/A'

  const bucket = MATURITY_BUCKETS.find((b) => b.key === bucketKey)
  return bucket ? bucket.label : bucketKey
}
